import React from 'react'

import { Icon } from '@mxenabled/mx-icons'
import { InstitutionLogo } from '@mxenabled/mxui'
import { H2, Text, P } from '@mxenabled/mxui'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'

import {
  DeepLinkDialog,
  type DeepLinkDialogCopyObject,
  type DeepLinkDrawerProps,
} from '../DeepLinkDialog'

import { Circle } from './Circle'
import { Dot } from './Dot'
import { Oval } from './Oval'

interface CopyObject extends DeepLinkDialogCopyObject {
  almost_there: string
  connecting: string
  error_description: string
  error_button_text: string
  error_title: string
  institution_logo_aria: string
  loading: string
  syncing_your_information: string
  were_getting_closer: string
  were_working_on_it: string
  your_direct_deposit_has_been_updated_successfully: string
}

interface DirectDepositDeepLinkProps extends DeepLinkDrawerProps {
  copy: CopyObject
  institution_guid: string
  isComplete: boolean
  isError: boolean
  isLoading: boolean
}

export const DirectDepositDeepLink: React.FC<DirectDepositDeepLinkProps> = ({
  copy,
  institution_guid,
  isComplete,
  isError,
  isLoading,
  ...rest
}) => {
  const [progressText, setProgressText] = React.useState(copy.were_working_on_it)
  const [textIndex, setTextIndex] = React.useState(0)

  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    if (textIndex <= 2) {
      const textToRotate = [copy.were_working_on_it, copy.were_getting_closer, copy.almost_there]
      const intervalId = setTimeout(() => {
        setTextIndex(textIndex + 1)
        setProgressText(textToRotate[textIndex])
      }, 4000)

      return () => clearTimeout(intervalId)
    }
  }, [copy, textIndex, progressText])

  return (
    <DeepLinkDialog copy={copy} {...rest}>
      <Stack sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 24 }}>
        <Circle borderColor="#E5E5E5" size={119}>
          <Circle borderColor="#B2B2B2" size={97}>
            <Circle borderColor="#808080" size={75}>
              <Oval />
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 4, zIndex: 1 }}>
                <InstitutionLogo
                  alt={copy.institution_logo_aria}
                  institutionGuid={institution_guid}
                  size={64}
                  style={{ borderRadius: '8px' }}
                />
                <Dot />
                <Dot />
                {isError ? (
                  <Icon color={'error'} fill={true} name="error" size={32} />
                ) : (
                  <Icon
                    fill={true}
                    name="nest_clock_farsight_analog"
                    size={32}
                    sx={{ color: 'text.secondary' }}
                  />
                )}
                <Dot />
                <Dot />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'background.paper',
                    backgroundColor: 'success.main',
                    borderRadius: '8px',
                    p: 8,
                    height: 64,
                    width: 64,
                  }}
                >
                  <Icon fill={true} name="monetization_on" size={48} />
                </Box>
              </Box>
            </Circle>
          </Circle>
        </Circle>
      </Stack>

      <Stack sx={{ alignItems: 'center', justifyContent: 'center' }}>
        {isComplete && !isError && (
          <H2 sx={{ textAlign: 'center' }}>
            {copy.your_direct_deposit_has_been_updated_successfully}
          </H2>
        )}
        {isLoading && (
          <>
            <H2>{copy.connecting}</H2>
            <Box sx={{ my: 24, width: '50%' }}>
              <LinearProgress aria-label={copy.loading} />
            </Box>
            <Text bold={true} variant="body1">
              {copy.syncing_your_information}
            </Text>
            <Text sx={{ mt: 8 }} variant="body2">
              {progressText}
            </Text>
          </>
        )}
        {isError && (
          <Stack sx={{ gap: 24, px: 24, pb: 24 }}>
            <Stack>
              <H2 sx={{ alignSelf: 'center' }}>{copy.error_title}</H2>
              <P
                dangerouslySetInnerHTML={{ __html: copy.error_description }}
                sx={{
                  fontSize: 15,
                  lineHeight: '24px',
                  px: '0px !important', //The important is necessary here px: 0 will not override the mui styling.
                  textAlign: 'center',
                  width: 352,
                }}
              />
            </Stack>
            <Button onClick={rest.onClose} sx={{ mx: 'auto' }} variant="contained">
              {copy.error_button_text}
            </Button>
          </Stack>
        )}
      </Stack>
    </DeepLinkDialog>
  )
}
